import { Inject, Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { BehaviorSubject, defaultIfEmpty, lastValueFrom, map, Observable, Subject, switchMap, tap } from 'rxjs';
import { CORE_CONFIG, CoreConfig } from '../core-config';
import { LanguageTypes, UserProfile, UserRoleV2, UsersApi, UsersV2Api, UserUpdateDTO } from '../my-account-sdk';
import { WorkspaceService } from './workspace.service';

export const SUPER_ADMIN_ROLE = 'super_admin';
export const ADMIN_ROLES = ['admin', SUPER_ADMIN_ROLE];
export const CREATOR_ROLES = ['curator', 'content', ...ADMIN_ROLES];

@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  private _fetchProfile = new Subject<void>();
  private _user = new BehaviorSubject<UserProfile>(null);
  private _profile = new BehaviorSubject<UserProfile>(undefined);
  private _roles = new BehaviorSubject<UserRoleV2[]>([]);

  readonly profile$ = this._profile.asObservable();
  readonly user$ = this._user.asObservable();
  readonly roles$ = this._roles.asObservable();

  /**
   * Return an unfiltered list of all the user's roles
   */
  get userRoles(): UserRoleV2[] {
    return this._roles.getValue();
  }

  get currentApplicationId(): string {
    return this._coreConfig.appId;
  }

  constructor(
    private _workspaceService: WorkspaceService,
    private _keycloakService: KeycloakService,
    private _usersApi: UsersApi,
    private _usersV2Api: UsersV2Api,
    @Inject(CORE_CONFIG) private _coreConfig: CoreConfig,
  ) {
    this.registerWorkspaceChange();
    this.registerFetchProfileSub();
  }

  private registerFetchProfileSub(): void {
    this._fetchProfile
      .asObservable()
      .pipe(switchMap(() => this._usersV2Api.userInfo()))
      .subscribe({
        next: (user) => {
          this._profile.next(user);
          this._user.next({ ...(user as any) });
          this._roles.next(user.roles);
        },
      });
  }

  private registerWorkspaceChange(): void {
    this._workspaceService.currentWorkspace$.subscribe(() => this.fetchProfile());
  }

  /**
   * Loads the current user's profile
   */
  fetchProfile(): void {
    this._fetchProfile.next();
  }

  initializeProfile() {
    return lastValueFrom(
      this._usersV2Api.userInfo().pipe(
        tap({
          next: (user) => {
            this._profile.next(user);
            this._user.next({ ...(user as any) });
            this._roles.next(user.roles);
          },
        }),
        defaultIfEmpty(null),
      ),
    );
  }

  /**
   * Loads the current user's profile
   */
  fetchUserData(): void {
    this._usersApi.fetchProfile().subscribe((user) => this._user.next(user));
  }

  /**
   * Returns a list of the user roles has in the current application and workspace
   * @param applicationId If provided, overrides which application the roles are evaluated from
   */
  getApplicationRoles(applicationId?: string): string[] {
    const appId = applicationId || this._coreConfig.appId;
    return this._roles
      .getValue()
      .filter((role) => role.application_id === appId)
      .map((item: any) => item?.key);
  }

  /**
   * Returns whether the current user has the keeps_admin keyCloak role
   */
  isKeepsAdmin(): boolean {
    return this.hasRoles(['keeps_admin']);
  }

  /**
   * Returns an observable of whether the current user has the keeps_admin keyCloak role
   */
  isKeepsAdmin$(): Observable<boolean> {
    return this.roles$.pipe(map(() => this.isKeepsAdmin()));
  }

  /**
   * Returns whether the current user has the keeps_platform_admin keyCloak role
   */
  isKeepsPlatformAdmin(): boolean {
    return this.hasRoles(['keeps_platform_admin']);
  }

  /**
   * Returns whether the current user has at least one of the informed roles in the current application and workspace or
   * Keycloak
   */
  hasRoles(roles: string[], appId?: string): boolean {
    if (!roles) {
      return false;
    }

    if (!this._keycloakService.getKeycloakInstance()) {
      return false;
    }

    const userRoles = this.getApplicationRoles(appId);
    const keycloakRoles = this._keycloakService.getUserRoles();
    return [...userRoles, ...keycloakRoles].some((role) => roles.includes(role));
  }

  /**
   * Returns whether the current user has at least one of the admin roles
   */
  isAdmin(): boolean {
    return this.hasRoles(ADMIN_ROLES);
  }

  /**
   * Returns an observable of whether the current user has at least one of the admin roles
   */
  isAdmin$(): Observable<boolean> {
    return this.roles$.pipe(map(() => this.isAdmin()));
  }

  /**
   * Returns whether the current user has the super admin role
   */
  isSuperAdmin(): boolean {
    return this.hasRoles([SUPER_ADMIN_ROLE]);
  }

  /**
   * Returns an observable of whether the current user has the super admin role
   */
  isSuperAdmin$(): Observable<boolean> {
    return this.roles$.pipe(map(() => this.isSuperAdmin()));
  }

  /**
   * Returns whether the current user has at least one of content creator roles
   */
  isContentCreator(): boolean {
    return this.hasRoles(CREATOR_ROLES);
  }

  /**
   * Returns an observable of whether the current user has at least one of content creator roles
   */
  isContentCreator$(): Observable<boolean> {
    return this.roles$.pipe(map(() => this.isContentCreator()));
  }

  /**
   * Returns whether the current user has all the informed roles in the current application
   */
  hasRequiredRoles(requiredRoles: string[]): boolean {
    if (!requiredRoles) {
      return false;
    }

    const userRoles = this.getApplicationRoles();
    return requiredRoles?.every((role) => userRoles.includes(role));
  }

  /**
   * Returns whether the user is an analytics leader but not an admin
   */
  isAnalyticsLeader(): boolean {
    const roles = this.getApplicationRoles();
    const ROLE_ADMIN = 'basic_analytics_admin';
    const ROLE_LEADER = 'basic_analytics_leader';
    return roles.includes(ROLE_LEADER) && !roles.includes(ROLE_ADMIN);
  }

  /**
   * Returns whether the user has the admin role in a specific application
   */
  isApplicationAdmin(applicationId: string): boolean {
    const roles = this.getApplicationRoles(applicationId);
    return roles?.some((role) => ADMIN_ROLES.includes(role));
  }

  /**
   * Returns the user's current locale
   */
  getUserLocale(): LanguageTypes {
    const locale = this._keycloakService.getKeycloakInstance()?.idTokenParsed['locale'];
    return locale || 'pt-BR';
  }

  /**
   * Returns the current user profile.
   */
  getProfile() {
    return this._profile.getValue();
  }

  updateProfile(profile: UserUpdateDTO): void {
    const userId = this.getProfile().id;
    this._usersApi.updateProfile(userId, profile).subscribe((user) => {
      this._user.next({ ...user });
    });
  }

  uploadAvatar(image: File) {
    const id = this.getProfile().id;
    this._usersApi.uploadAvatar(id, image).subscribe((user) => this._user.next({ ...user }));
  }
}
